<template>
  <div v-if="funnelData" class="layout">
    <div class="funnels__content--wrap">
      <h3 class="txt--title">
        <ui-button
          circle
          iconName="icon--inflow__back"
          @click.prevent="$router.push({ name: 'funnel.list', params: { aid: $route.params.aid } })">
        </ui-button>
        <span>{{ funnelData.name }}</span>
      </h3>
      <div v-if="items.length > 5" class="funnels__collapse--wrap"><!-- 5개이상 노출시 버튼 활성화 -->
        <ui-button v-if="isCompactMode"
                   primary2
                   :iconName="'icon--expand'"
                   @click="setCompactMode(false)"
                   class="btn_expand">
          {{ $t('common.expand' )}}
        </ui-button>

        <ui-button v-else
                   primary
                   :iconName="'icon--collapse'"
                   @click="setCompactMode(true)"
                   class="btn_collapse">
          {{ $t('common.collapse' )}}
        </ui-button>

        <div v-if="!isCompactMode" class="common_btn">
          <button class="btn_direction"
            :class="{ disable: scrollIndex === 0 }"
            @click="onPrevItemClick"><span class="icon--prev"></span></button>
          <button class="btn_direction"
            :class="{ disable: scrollIndex === maxScrollIndex }"
            @click="onNextItemClick"><span class="icon--next"></span></button>
        </div>
      </div>
      <div class="box__stop--wrap" v-if="stoppedData">
        <span v-if="stoppedData.count === 1"
          v-html="$t('funnel.funnelsItem.stopDataCollection.desc.single', {
            stopDate: $options.filters.dotDate(stoppedData.stopDate),
          })"></span>
        <span v-else
          v-html="$t('funnel.funnelsItem.stopDataCollection.desc.plural', {
            stopDate: $options.filters.dotDate(stoppedData.stopDate),
            // stoppedStep: stoppedData.stoppedStep,
            stoppedCount: stoppedData.stoppedCount,
            totalStep: stoppedData.totalStep
          })"></span>
      </div>
      <div class="funnels__option--wrap">
        <div class="funnels__control__device" :class="{ 'mobile-only': bMobile }">
          <button v-for="item in deviceList" :key="item"
            class="item" :class="{ on: device === item }"
            @click="$emit('onDeviceChange', item)">
            <i class="icon" :class="`icon--device-${item}`"></i>
            <template v-if="bMobile">
              <span class="text device_name">{{ $t('common.deviceType.phone') }}</span>
              <span class="delimiter"></span>
            </template>
            <span class="text percentage" v-if="infoData">{{ getDevicePvPercentage(item) | fixed(1) }}%</span>
            <span class="text percentage" v-else>0%</span>
          </button>
        </div>
        <div class="funnels__control__checkbox">
          <ui-checkbox :value="showNew" @input="onDataTypeToggle('new')">
            {{ $t('common.new') }}
          </ui-checkbox>
          <ui-checkbox :value="showRevisit" @input="onDataTypeToggle('revisit')">
            {{ $t('common.revisit') }}
          </ui-checkbox>
        </div>
        <div class="funnels__control__info">
          <span class="control__info">
            <span class="icon--sessions"></span>
            <span class="txt--desc">
              {{ $tc('common.sessions', funnelData.sessionCount[dataType]) }} :
            </span>
            <span class="txt--num">{{ funnelData.sessionCount[dataType] | thousand }}</span>
          </span>
          <span class="bar"></span>
          <span class="control__info">
            <span class="icon--rate"></span>
            <span class="txt--desc">
              {{ $t('funnel.funnelsItem.conversionRate') }} :
            </span>
            <span class="txt--num txt--item">{{ funnelData.conversionRate[dataType] | fixed(1) }}%</span>
          </span>
          <span class="bar"></span>
          <div class="control__info">
            <span class="txt--desc">
              {{ $t('common.totalSteps') }} :
            </span>
            <span class="txt--num txt--item2">{{ items.length }}</span>
          </div>
        </div>
      </div>
      <funnels-item v-if="items.length"
        :dataType="dataType"
        :items="items"
        :pathItems="funnelData.pathItems"
        :countData="funnelData.sessionCount"
        :isCompactMode="isCompactMode"
        :scrollIndex="scrollIndex"
        @onShowFlowClick="onShowFlowClick"
        @onConversionClick="onConversionClick"
      />
    </div>

    <funnels-layer v-if="flowData"
      :flowData="flowData"
      @onCloseClick="flowData = null"/>

    <funnels-layer v-if="deletedItems && deletedItems.length"
      :deletedItems="deletedItems"
      @onCloseClick="$router.push({ name: 'funnel.list', params: { aid: $route.params.aid } })"/>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { pick, getUrlWithoutProtocol, getPercentage } from '@/libs/utils'
import { getFunnelFlow } from '@/api/funnel'
import UiCheckbox from '@/components/ui/UiCheckbox'
import FunnelsLayer from '@/components/funnels/FunnelsLayer'
import FunnelsItem from '@/components/funnels/FunnelsItem'
import UiButton from '@/components/ui/UiButton'

function getCurvedPath (startY, endY, reverse, width = 100) {
  const start = {
    x: 0,
    y: startY
  }
  const end = {
    x: width,
    y: endY
  }

  const cp1 = {
    x: width * 0.5,
    y: start.y
  }
  const cp2 = {
    x: width * 0.5,
    y: end.y
  }

  let d

  if (reverse) {
    d = `C ${cp2.x} ${cp2.y}, ${cp1.x} ${cp1.y}, ${start.x} ${start.y}`
  } else {
    d = `C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${end.x} ${end.y}`
  }
  return d
}

const inflowFieldMap = {
  all: 'inflow',
  new: 'inflowNew',
  revisit: 'inflowExist'
}

const droppedFieldMap = {
  all: 'dropped',
  new: 'droppedNew',
  revisit: 'droppedExist'
}

export default {
  name: 'FunnelsItemView',
  components: {
    UiCheckbox,
    FunnelsLayer,
    FunnelsItem,
    UiButton
  },
  props: [
    'rawData',
    'device',
    'infoData'
  ],
  data () {
    return {
      showNew: true,
      showRevisit: true,
      flowData: null,
      isCompactMode: false,
      scrollIndex: 0
    }
  },
  computed: {
    ...mapState([
      'reportPeriod',
      'applyData',
      'userData'
    ]),
    dataType () {
      if (this.showNew && this.showRevisit) {
        return 'all'
      } else if (this.showNew) {
        return 'new'
      } else {
        return 'revisit'
      }
    },
    funnelData () {
      if (this.rawData) {
        const items = this.rawData.funnelList
        const firstItem = items[0]
        const lastItem = items[items.length - 1]

        const sessionCount = {}
        const conversionRate = {}

        for (const [key, inflowField] of Object.entries(inflowFieldMap)) {
          sessionCount[key] = firstItem[inflowField]
          conversionRate[key] = getPercentage(lastItem[inflowField], firstItem[inflowField])
        }

        const data = {
          name: this.rawData.name,
          sessionCount,
          conversionRate,
          items: items.map((item, index) => {
            if (!item.matchType) {
              item.matchType = 'exact'
            }
            const inflow = {}
            const dropped = {}
            const dropOff = {}
            const dropOffRate = {}
            const remain = {}
            const isLast = index === items.length - 1
            let newPercentage = 0
            let revisitPercentage = 0

            for (const [key, inflowField] of Object.entries(inflowFieldMap)) {
              const dropField = droppedFieldMap[key]
              inflow[key] = item[inflowField]
              dropped[key] = item[dropField]
              dropOffRate[key] = getPercentage(item[dropField], sessionCount[key])

              if (isLast) {
                dropOff[key] = getPercentage(item[inflowField], sessionCount[key])
              } else {
                dropOff[key] = getPercentage(item[dropField], item[inflowField])
              }
              remain[key] = getPercentage(item[inflowField], sessionCount[key])
            }

            if (this.dataType !== 'revisit') {
              newPercentage = getPercentage(inflow['new'], sessionCount[this.dataType])
            }

            if (this.dataType !== 'new') {
              revisitPercentage = getPercentage(inflow['revisit'], sessionCount[this.dataType])
            }

            return {
              inflow,
              dropped,
              dropOff,
              dropOffRate,
              remain,
              newPercentage,
              revisitPercentage,
              ...pick(item, [
                'aid',
                'bMobile',
                'deleted',
                'imagePath',
                'thumbImagePath',
                'matchType',
                'name',
                'state',
                'url',
                'stopDate'
              ]),
              step: index + 1
            }
          })
        }

        data.pathItems = this.getPathItems(data.items, data.sessionCount)
        return data
      }
      return null
    },
    items () {
      return this.funnelData ? this.funnelData.items : null
    },
    deletedItems () {
      if (this.items) {
        return this.items.filter(item => item.deleted)
      }
      return null
    },
    stoppedData () {
      if (this.items) {
        const stoppedItems = this.items.filter(item => item.state === 'stop')
          .sort((a, b) => {
            return a.stopDate >= b.stopDate ? 1 : -1
          })

        const stoppedCount = stoppedItems.length

        if (stoppedCount) {
          return {
            stopDate: stoppedItems[0].stopDate,
            stoppedStep: stoppedItems[0].step,
            stoppedCount: stoppedCount,
            totalStep: this.items.length
          }
        }
      }
      return null
    },
    maxScrollIndex () {
      return this.items.length - 5
    },
    bMobile () {
      return this.funnelData.items[0]['bMobile']
    },
    deviceList () {
      if (this.bMobile) {
        return ['phone']
      } else {
        return ['desktop', 'tablet', 'phone']
      }
    }
  },
  created () {
  },
  methods: {
    setCompactMode (value) {
      this.isCompactMode = value

      if (!this.isCompactMode) {
        this.scrollIndex = 0
      }
    },
    onDataTypeToggle (type) {
      if (type === 'new') {
        this.showNew = !this.showNew

        if (!this.showNew && !this.showRevisit) {
          this.showRevisit = true
        }
      } else {
        this.showRevisit = !this.showRevisit

        if (!this.showRevisit && !this.showNew) {
          this.showNew = true
        }
      }
    },
    onItemHover (item, isOn) {
      this.$set(item, 'isExpanded', false)
      this.$set(item, 'isOn', isOn)
    },
    onItemTransitionEnd (item, isHover) {
      this.$set(item, 'isExpanded', isHover)
    },
    onPrevItemClick () {
      this.scrollIndex = Math.max(0, this.scrollIndex - 1)
    },
    onNextItemClick () {
      this.scrollIndex = Math.min(this.maxScrollIndex, this.scrollIndex + 1)
    },
    getPathItems (items) {
      const dataType = this.dataType

      function getPathData (item) {
        const itemPercentage = item.remain[dataType]
        const newPercentage = item.newPercentage
        const revisitPercentage = item.revisitPercentage
        const top = (100 - itemPercentage) / 2
        const result = {
          top,
          bottom: top + itemPercentage
        }

        if (dataType === 'all') {
          result.middle = top + newPercentage
          item.newLabelPercentage = result.middle - (newPercentage / 2)
          item.revisitLabelPercentage = result.middle + (revisitPercentage / 2)

          if (item.revisitLabelPercentage - item.newLabelPercentage <= 10) {
            item.newLabelPercentage = item.newLabelPercentage - 5
            item.revisitLabelPercentage = item.revisitLabelPercentage + 5
          }
        }
        return result
      }

      const pathItems = []

      for (let i = 0; i < items.length; i++) {
        const current = items[i]
        const next = items[i + 1] || current
        const start = getPathData(current)
        const end = getPathData(next)
        const topCurvedPath = getCurvedPath(start.top, end.top)
        const middleCurvedPath = getCurvedPath(start.middle, end.middle, true)
        const bottomCurvedPath = getCurvedPath(start.bottom, end.bottom, dataType !== 'all')

        let newPath
        let revisitPath

        if (dataType === 'all') {
          newPath = `M 0 ${start.top} ${topCurvedPath} L 100 ${end.middle} ${middleCurvedPath} L 0 ${start.top} Z`
          revisitPath = `M 0 ${start.bottom} ${bottomCurvedPath} L 100 ${end.middle} ${middleCurvedPath} L 0 ${start.bottom} Z`
        } else if (dataType === 'new') {
          newPath = `M 0 ${start.top} ${topCurvedPath} L 100 ${end.bottom} ${bottomCurvedPath} L 0 ${start.top} Z`
        } else if (dataType === 'revisit') {
          revisitPath = `M 0 ${start.top} ${topCurvedPath} L 100 ${end.bottom} ${bottomCurvedPath} L 0 ${start.top} Z`
        }

        pathItems.push({
          newPath,
          revisitPath
        })
      }
      return pathItems
    },
    onConversionClick (item, isDropped, isConverted) {
      const aid = item.aid
      const index = this.items.findIndex(item => item.aid === aid)

      const to = {
        name: 'funnel.sessionList',
        params: {
          aid: this.$route.params.aid,
          fid: this.$route.params.fid,
          timezoneName: this.userData.timezoneName
        },
        query: {
          seq: index,
          dropped: isDropped,
          converted: isConverted,
          timezoneName: this.userData.timezoneName,
          device: this.$route.query.device,
          faid: this.$route.query.faid
        }
      }
      this.$router.push(to)
    },
    onShowFlowClick (item) {
      const params = {
        aid: item.aid,
        startDate: this.reportPeriod.start,
        endDate: this.reportPeriod.end
      }
      const stepData = {}

      if (this.items) {
        for (let i = 0; i < this.items.length; i++) {
          const stepItem = this.items[i]
          const stepUrl = getUrlWithoutProtocol(stepItem.url)

          if (!stepData[stepUrl]) {
            stepData[stepUrl] = {
              step: i + 1,
              name: stepItem.name
            }
          }
        }
      }

      getFunnelFlow(params).then(data => {
        this.flowData = {
          ...data,
          domain: this.rawData.domain,
          stepData,
          funnel: pick(item, [
            'aid',
            'step',
            'name',
            'url',
            'matchType'
          ])
        }
      }).catch(err => {
        console.error(err)
      })
    },
    getDevicePvPercentage (item) {
      if (this.infoData.pv > 0 && this.infoData['device'][item]) {
        const pv = this.infoData['device'][item]['pv']
        return (pv / this.infoData.pv) * 100
      } else {
        return 0
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.layout {
  overflow-y: auto;
}

// 전체를 감싸는 래퍼
.funnels__content--wrap{
  overflow-x:hidden;
  padding:40px 0 40px 30px;
  .txt--title{
    font-size:22px;
    line-height:22px;
    color:#444;

    .ui-btn {
      vertical-align: middle;
      margin-right: 15px;
    }
    span {
      vertical-align: middle;
    }
  }
  .funnels__collapse--wrap{
    display:flex;
    padding-right:30px;
    justify-content: flex-end;
    .btn_expand{
      &:hover /deep/ .icon--expand {
        background-image: url(../../assets/img/funnel/funnel_expand_icon_hover.svg);
      }
    }
   .common_btn {
      position:relative;
      width: 83px;
      height: 38px;
      border-radius: 20px;
      box-shadow: 0 12px 13px -6px rgba(0, 0, 0, 0.15);
      background-color: #ec0047;
      &:after{
        position:absolute;
        top:0;
        left:41px;
        content:'';
        width: 1px;
        height: 38px;
        opacity: 0.32;
        background-color: #fff;
      }
      .btn_direction{
        display:inline-block;
        width:41px;
        height:38px;
        .icon--prev{
          display:inline-block;
          vertical-align: middle;
        }
        .icon--next{
          display:inline-block;
          vertical-align: middle;
        }
          &.disable {
          opacity: 0.4
        }
      }
   }
   .btn_collapse + .common_btn{
     margin-left:10px;
   }
  }
  .box__stop--wrap{
    position:relative;
    margin-right:30px;
    margin-top: 15px;
    padding:10px 20px 10px 35px;
    background:#fff;
    border-radius: 8px;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
    color: #e92020;
    &:after{
      content:'';
      position:absolute;
      top:50%;
      left:11px;
      width:16px;
      height:16px;
      margin-top:-8px;
      background:url(../../assets/img/funnel/funnel_stop_icon.svg) no-repeat;
    }
  }
  .funnels__option--wrap{
    display:flex;
    align-items: center;
    margin-top:33px;
    .funnels__control__device{
      position: relative;
      white-space: nowrap;
      display: inline-block;
      width: 261px;
      height: 38px;
      border-radius: 20px;
      border: solid 1px #e0e0e0;
      background-color: #ffffff;
      box-sizing: border-box;
      margin-right: 15px;

      button {
        position: absolute;
        top: 0;
        width: 91px;
        height: 100%;
        border-radius: 20px;
        background-color: #fff;
        overflow: visible;

        &> span {
          vertical-align: middle;
        }

        &:hover:not(.on) .text {
          color: #444;
        }

        &:nth-of-type(1) {
          left: 0;
          &.on {
            left: -1px;
          }
          &:hover:not(.on) i.icon--device-desktop {
            background-image: url(../../assets/img/heatmap/heatmap_device_desktop_hover.svg);
          }
        }

        &:nth-of-type(2) {
          left: 50%;
          transform: translateX(-50%);
          &.on {
            top: -1px;
          }
          &:hover:not(.on) i.icon--device-tablet  {
            background-image: url(../../assets/img/heatmap/heatmap_device_tablet_hover.svg);
          }
        }

        &:nth-of-type(3) {
          right: 0;
          &.on {
            right: -1px;
          }
          &:hover:not(.on) i.icon--device-phone {
            background-image: url(../../assets/img/heatmap/heatmap_device_phone_hover.svg);
          }
        }

        &:focus {
          z-index: 1;
        }

        &.on {
          top: -1px;
          height: 38px;
          box-shadow: 0 12px 10px -4px rgba(0, 0, 0, 0.2);
          background-color: $brandColor;
          z-index: 2;
          pointer-events: none;
          .text {
            color: #fff;
          }
        }

        i {
          vertical-align: middle;
          margin-right: 2px;
        }
        .text {
          font-size: 13px;
          font-weight: 500;
          color: #888888;
        }
        .percentage {
          display: inline-block;
          width: 42px;
          text-align: center;
        }
        .device_name {
          margin-left: 5px;
        }
        .delimiter {
          display: inline-block;
          width: 1px;
          height: 11px;
          background-color: #fff;
          opacity: 0.5;
          margin-right: 4px;
          margin-left: 8px;
        }
      }

      &.mobile-only {
        width: 156px;
        button {
          width: 156px;
        }
      }
    }
    .funnels__control__checkbox{
      .ui_checkbox{
        white-space: nowrap;
        /deep/ .text{
          color:#89b600;
        }
      }
      .ui_checkbox + .ui_checkbox{
         /deep/ .text{
          color:#1199af;
        }
      }
    }
  }
  .funnels__collapse--wrap + .funnels__option--wrap {
    margin-top:0;
  }
  //상단 우측
  .funnels__control__info{
   margin-left:35px;
  .control__info{
      display:inline-block;
    .icon--sessions{
      display:inline-block;
      vertical-align: middle;
    }
    .icon--rate{
      display:inline-block;
      vertical-align: middle;
    }
    .txt--desc{
      display:inline-block;
      padding-left:8px;
      font-size:15px;
      vertical-align: middle;
    }
    .txt--num{
      display:inline-block;
      padding-left:6px;
      font-size:15px;
      color:#1fb59b;
      vertical-align: middle;
    }
    .txt--item{
      color:#289eef
    }
    .txt--item2{
      color:#666;
    }
  }
  .control__info:last-of-type{
    .txt--desc{
      padding-left:0;
    }
  }
  .bar{
      display:inline-block;
      width: 1px;
      height: 12px;
      background-color: #d7d7d7;
      margin: 2px 15px 0 15px;
      vertical-align: middle;
    }
  }
}
</style>
